/**
 * Tenant booking-settings form card (DNKADM-0050) — the editable form for
 * `GET/PUT /v2/admin/tenant-settings` (handoff §5.1). Seeded from the loaded view
 * and saved through `putTenantSettings`, which **never sends `currency`** (read-only
 * on this surface — the currency lives in a separate card).
 *
 * Fields (with client validation mirroring the server's positive-integer ranges):
 *  - **Slot interval** — `serviceIntervalMinutes`, positive integer minutes;
 *  - **Booking window** — `bookingWindowMonths`, positive integer months;
 *  - **Booking lead time** — `leadTimeMinutes` (DNKADM-0385), blank (no lead time)
 *    or a whole number with a Minutes / Hours / Days unit (DNKADM-0389 U4), converted
 *    to minutes on save and shown in the largest even unit on load; written through
 *    the billing-day sentinel (`0` = clear, `n` = set, always sent) — see
 *    `@/domain/leadTime` and `@/components/LeadTimeInput`;
 *  - **Guest hold** — `guestHoldTtlMinutes`, positive integer minutes;
 *  - **Default timezone** — IANA tz chosen from the same strict `Autocomplete`
 *    picker as the Locations dialog (DNKADM-0077/DNKADM-0083) — sourced from the
 *    shared `listTimeZones()`; no invalid free-text save; a legacy/invalid stored
 *    value is preserved on load (location owns per-slot tz, §4.5; no
 *    `@mui/x-date-pickers`);
 *  - **Country** — `countryCode` (DNKADM-0267), a validated ISO 3166-1 alpha-2
 *    text field (auto-uppercased, 2 letters or blank). Partial-update: a blank is
 *    OMITTED from the PUT (the stored value is unchanged — the backend has no
 *    unset path on this surface).
 *  - **Coach calendar sync** — `staffCalendarProvider` (DNKADM-0384 U1), a three-way
 *    choice in plain words (off / through the white-labeled platform / direct Google;
 *    labels from `@/utils/whitelabel`, never a code). Always sent as its wire value.
 *    Off is the default and means no coach syncs whatever their own setting says —
 *    the row says so. Changing it never clears a coach's calendar selection.
 *  - **Only members can buy credits in the member app** — `creditPurchaseMembersOnly`
 *    (DNKADM-0409, DNKAPI-0566), a switch that saves ON ITS OWN when flipped: the same
 *    `PUT /tenant-settings` carrying only that key (partial update), the same success
 *    toast and error classification. Not optimistic — the switch shows the value from
 *    the PUT response, so a failed PUT leaves the previous value showing. It does not
 *    refetch (a refetch remounts the card and would drop unsaved edits in the form), and
 *    "Save settings" never sends the key.
 *
 * One "Save settings" → `PUT /tenant-settings`. A failed field blocks Save. On a
 * successful save the parent `refetch`es (refetch-after-write reseeds) and a success
 * toast is raised. A validation `400` renders INLINE; an upstream/`502`/`403` raises
 * an error toast (see `classifyFormError`).
 */
import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MONO, resolveTokens } from '@/theme';
import type { ShowToast } from '@/components/toastContext';
import {
  putTenantSettings,
  type HoldAutoAction,
  type TenantSettingsView,
} from '@/api/tenantSettings';
// Reuse the one shared IANA picker source (DNKADM-0077) — no second copy.
import { listTimeZones } from '@/features/locations/timezones';
import {
  LEAD_TIME_BEHAVIOUR,
  parseLeadTimeEntry,
  splitLeadTime,
  type LeadTimeUnit,
} from '@/domain/leadTime';
import { LeadTimeInput } from '@/components/LeadTimeInput';
import {
  STAFF_CALENDAR_PROVIDERS,
  normalizeStaffCalendarProvider,
  staffCalendarProviderDescription,
  staffCalendarProviderLabel,
  type StaffCalendarProvider,
} from '@/utils/whitelabel';
import { classifyFormError } from '@/api/screenError';

/** A failed save: a rejected field inline, anything else a toast (DNKADM-0440). */
function classifySaveError(err: unknown) {
  return classifyFormError(err, 'That value isn’t valid — check the highlighted fields.', {
    toastFallback: 'The settings couldn’t be saved. Please try again.',
    conflictInline: false,
  });
}

interface TenantSettingsCardProps {
  /** The loaded booking settings. */
  data: TenantSettingsView;
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  serviceIntervalMinutes?: string;
  bookingWindowMonths?: string;
  guestHoldTtlMinutes?: string;
  holdAutoActionMinutes?: string;
  holdAutoAction?: string;
  defaultTimezone?: string;
  countryCode?: string;
  billingAnchorDay?: string;
  leadTimeMinutes?: string;
}

/** A positive-integer validity check on a typed string field. */
function positiveInt(raw: string): number | null {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
  return n;
}

/**
 * Billing anchor day validation (DNKADM-0374): blank → valid (sentinel `0` on PUT);
 * whole number 1–28 → valid (that integer); anything else → invalid.
 * Rejects `0`, `29`, fractions, and non-numeric text inline — never hits the wire.
 */
function parseBillingDay(raw: string): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null }; // blank = clear/leave
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 28) return { ok: false };
  return { ok: true, value: n };
}

/**
 * Validate the booking-settings fields against the server's positive-integer
 * ranges + a non-empty IANA timezone. Returns the parsed values when valid, else
 * the per-field error messages.
 */
function validate(
  serviceIntervalMinutes: string,
  bookingWindowMonths: string,
  guestHoldTtlMinutes: string,
  defaultTimezone: string,
  holdAutoEnabled: boolean,
  holdAutoActionMinutes: string,
  holdAutoAction: HoldAutoAction | null,
  countryCode: string,
  billingAnchorDayRaw: string,
  leadTimeAmountRaw: string,
  leadTimeUnit: LeadTimeUnit,
):
  | {
      ok: true;
      values: {
        serviceIntervalMinutes: number;
        bookingWindowMonths: number;
        guestHoldTtlMinutes: number;
        defaultTimezone: string;
        holdAutoActionMinutes: number;
        holdAutoAction: HoldAutoAction | null;
        /** Two uppercase letters, or `undefined` when blank (key omitted — unchanged). */
        countryCode?: string;
        /** 0 = clear, 1–28 = set; always present on this form's PUT. */
        billingAnchorDay: number;
        /** 0 = clear (no lead time), 1–MAX = set; always present on this form's PUT (DNKADM-0385). */
        leadTimeMinutes: number;
      };
    }
  | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {};

  const interval = positiveInt(serviceIntervalMinutes);
  if (interval === null) errors.serviceIntervalMinutes = 'Enter a whole number of minutes, 1 or more.';

  const window = positiveInt(bookingWindowMonths);
  if (window === null) errors.bookingWindowMonths = 'Enter a whole number of months, 1 or more.';

  const hold = positiveInt(guestHoldTtlMinutes);
  if (hold === null) errors.guestHoldTtlMinutes = 'Enter a whole number of minutes, 1 or more.';

  const holdAutoMinutes = holdAutoEnabled ? positiveInt(holdAutoActionMinutes) : 0;
  if (holdAutoEnabled && holdAutoMinutes === null) {
    errors.holdAutoActionMinutes = 'Enter a whole number of minutes, 1 or more.';
  }
  if (holdAutoEnabled && holdAutoAction === null) {
    errors.holdAutoAction = 'Choose Showed or No-show.';
  }

  const tz = defaultTimezone.trim();
  if (tz === '') errors.defaultTimezone = 'Enter an IANA timezone (e.g. America/New_York).';

  // Country (DNKADM-0267): blank is valid (the key is omitted — the stored value is
  // left unchanged); otherwise exactly two letters, normalized to uppercase.
  const country = countryCode.trim().toUpperCase();
  if (country !== '' && !/^[A-Z]{2}$/.test(country)) {
    errors.countryCode = 'Enter a two-letter country code, or leave blank.';
  }

  // Billing anchor day (DNKADM-0374): blank → 0 (clear/leave); 1–28 → that day.
  const billingDayResult = parseBillingDay(billingAnchorDayRaw);
  if (!billingDayResult.ok) {
    errors.billingAnchorDay = 'Enter a whole number from 1 to 28, or leave blank.';
  }

  // Booking lead time (DNKADM-0385, units DNKADM-0389): blank or 0 → 0 (clear: no lead
  // time); a whole number in the chosen unit, within the server ceiling → the minutes.
  const leadTimeResult = parseLeadTimeEntry(leadTimeAmountRaw, leadTimeUnit);
  if (!leadTimeResult.ok) errors.leadTimeMinutes = leadTimeResult.message;

  if (
    errors.serviceIntervalMinutes ||
    errors.bookingWindowMonths ||
    errors.guestHoldTtlMinutes ||
    errors.holdAutoActionMinutes ||
    errors.holdAutoAction ||
    errors.defaultTimezone ||
    errors.countryCode ||
    errors.billingAnchorDay ||
    errors.leadTimeMinutes
  ) {
    return { ok: false, errors };
  }
  // billingDayResult.ok is true here; value is null (blank) or 1–28.
  // Blank → sentinel 0 (clear/leave); filled → that day.
  const billingAnchorDay = billingDayResult.ok && billingDayResult.value !== null
    ? billingDayResult.value
    : 0;
  // Same sentinel for the lead time: blank → 0 (clear); filled → the minutes.
  const leadTimeMinutes = leadTimeResult.ok && leadTimeResult.value !== null ? leadTimeResult.value : 0;
  return {
    ok: true,
    // The three numbers are non-null here (each error path returned above).
    values: {
      serviceIntervalMinutes: interval as number,
      bookingWindowMonths: window as number,
      guestHoldTtlMinutes: hold as number,
      defaultTimezone: tz,
      holdAutoActionMinutes: holdAutoEnabled ? (holdAutoMinutes as number) : 0,
      holdAutoAction: holdAutoEnabled ? holdAutoAction : null,
      ...(country !== '' ? { countryCode: country } : {}),
      billingAnchorDay,
      leadTimeMinutes,
    },
  };
}

export function TenantSettingsCard({ data, refetch, showToast }: TenantSettingsCardProps) {
  const ids = useId();

  // Numeric fields edit as strings so an in-progress entry isn't clobbered; parsed
  // + range-checked on Save.
  const [serviceIntervalMinutes, setServiceIntervalMinutes] = useState(
    String(data.serviceIntervalMinutes),
  );
  const [bookingWindowMonths, setBookingWindowMonths] = useState(String(data.bookingWindowMonths));
  const [guestHoldTtlMinutes, setGuestHoldTtlMinutes] = useState(String(data.guestHoldTtlMinutes));
  const [holdAutoEnabled, setHoldAutoEnabled] = useState(data.holdAutoActionMinutes > 0);
  const [holdAutoActionMinutes, setHoldAutoActionMinutes] = useState(
    String(data.holdAutoActionMinutes > 0 ? data.holdAutoActionMinutes : 15),
  );
  const [holdAutoAction, setHoldAutoAction] = useState<HoldAutoAction | null>(data.holdAutoAction);
  const [defaultTimezone, setDefaultTimezone] = useState(data.defaultTimezone);
  // Country (DNKADM-0267) — preloads the stored ISO alpha-2 value; blank means
  // unconfigured (and stays off the PUT body).
  const [countryCode, setCountryCode] = useState(data.countryCode ?? '');
  // Billing anchor day (DNKADM-0374) — blank when null/unset; '1'–'28' when set.
  const [billingAnchorDay, setBillingAnchorDay] = useState(
    data.billingAnchorDay !== null ? String(data.billingAnchorDay) : '',
  );
  // Booking lead time (DNKADM-0385; units DNKADM-0389) — blank when null/unset (no lead
  // time); when set, the largest unit that divides the stored minutes evenly (120 → 2
  // Hours, 90 → 90 Minutes, 1440 → 1 Day). No default number is ever seeded.
  const [leadTimeAmount, setLeadTimeAmount] = useState(
    data.leadTimeMinutes !== null ? String(splitLeadTime(data.leadTimeMinutes).amount) : '',
  );
  const [leadTimeUnit, setLeadTimeUnit] = useState<LeadTimeUnit>(
    data.leadTimeMinutes !== null ? splitLeadTime(data.leadTimeMinutes).unit : 'minutes',
  );
  // Coach calendar sync provider (DNKADM-0384 U1) — a choice, not a toggle; 'none' = off.
  const [staffCalendarProvider, setStaffCalendarProvider] = useState<StaffCalendarProvider>(
    data.staffCalendarProvider,
  );

  // The canonical IANA list, plus the current value when it isn't on it — so an
  // already-stored legacy zone (e.g. an old `"Chicago/Central"`) loads as the
  // selection and is visibly correctable rather than silently blanked. No data is
  // rewritten here — mirrors LocationDialog (DNKADM-0077).
  const timeZoneOptions = useMemo(() => {
    const zones = listTimeZones();
    return defaultTimezone !== '' && !zones.includes(defaultTimezone)
      ? [defaultTimezone, ...zones]
      : zones;
  }, [defaultTimezone]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Member-app credit switch (DNKADM-0409) — the SAVED value; only a PUT response moves it.
  const [creditPurchaseMembersOnly, setCreditPurchaseMembersOnly] = useState(
    data.creditPurchaseMembersOnly,
  );
  const [savingCreditSwitch, setSavingCreditSwitch] = useState(false);
  // Unconfirmed-booking cancel refund switch (DNKADM-0432) — same saved-value, own-PUT shape.
  const [unconfirmedCancelFullRefund, setUnconfirmedCancelFullRefund] = useState(
    data.unconfirmedCancelFullRefund,
  );
  const [savingRefundSwitch, setSavingRefundSwitch] = useState(false);
  const busy = saving || savingCreditSwitch || savingRefundSwitch;

  async function handleCreditSwitch(next: boolean) {
    setServerError(null);
    setSavingCreditSwitch(true);
    try {
      const saved = await putTenantSettings({ creditPurchaseMembersOnly: next });
      setCreditPurchaseMembersOnly(saved.creditPurchaseMembersOnly);
      showToast('Tenant settings saved', 'success');
    } catch (err) {
      // Not optimistic: the switch still shows the previous saved value.
      const view = classifySaveError(err);
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSavingCreditSwitch(false);
    }
  }

  async function handleRefundSwitch(next: boolean) {
    setServerError(null);
    setSavingRefundSwitch(true);
    try {
      const saved = await putTenantSettings({ unconfirmedCancelFullRefund: next });
      setUnconfirmedCancelFullRefund(saved.unconfirmedCancelFullRefund);
      showToast('Tenant settings saved', 'success');
    } catch (err) {
      // Not optimistic: the switch still shows the previous saved value.
      const view = classifySaveError(err);
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSavingRefundSwitch(false);
    }
  }

  async function handleSave() {
    setServerError(null);
    const result = validate(
      serviceIntervalMinutes,
      bookingWindowMonths,
      guestHoldTtlMinutes,
      defaultTimezone,
      holdAutoEnabled,
      holdAutoActionMinutes,
      holdAutoAction,
      countryCode,
      billingAnchorDay,
      leadTimeAmount,
      leadTimeUnit,
    );
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      // NEVER sends `currency` — putTenantSettings omits the key entirely (§5.1).
      // `countryCode` goes out only when set (partial-update, DNKADM-0267).
      // `billingAnchorDay` always goes out: 0 = clear/leave, 1–28 = set (DNKADM-0374).
      await putTenantSettings({
        serviceIntervalMinutes: result.values.serviceIntervalMinutes,
        bookingWindowMonths: result.values.bookingWindowMonths,
        guestHoldTtlMinutes: result.values.guestHoldTtlMinutes,
        holdAutoActionMinutes: result.values.holdAutoActionMinutes,
        holdAutoAction: result.values.holdAutoAction,
        defaultTimezone: result.values.defaultTimezone,
        ...(result.values.countryCode !== undefined
          ? { countryCode: result.values.countryCode }
          : {}),
        billingAnchorDay: result.values.billingAnchorDay,
        // `leadTimeMinutes` always goes out too: 0 = clear, 1–MAX = set (DNKADM-0385).
        leadTimeMinutes: result.values.leadTimeMinutes,
        // The coach calendar provider always goes out as its wire value (DNKADM-0384 U1).
        staffCalendarProvider,
      });
      refetch();
      showToast('Tenant settings saved', 'success');
    } catch (err) {
      const view = classifySaveError(err);
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const intervalId = `${ids}-interval`;
  const windowId = `${ids}-window`;
  const holdId = `${ids}-hold`;
  const holdAutoId = `${ids}-hold-auto`;
  const tzId = `${ids}-tz`;
  const countryId = `${ids}-country`;
  const billingDayId = `${ids}-billing-day`;
  const leadTimeId = `${ids}-lead-time`;
  const calendarProviderId = `${ids}-calendar-provider`;

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
        <SettingRow label="Slot interval" hint="Granularity of bookable time slots">
          <TextField
            id={intervalId}
            hiddenLabel
            value={serviceIntervalMinutes}
            onChange={(e) => setServiceIntervalMinutes(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.serviceIntervalMinutes)}
            helperText={fieldErrors.serviceIntervalMinutes}
            sx={{ width: 170 }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
                sx: { fontFamily: MONO },
              },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Slot interval (minutes)' },
            }}
          />
        </SettingRow>

        <SettingRow label="Booking window" hint="How far ahead members may book">
          <TextField
            id={windowId}
            hiddenLabel
            value={bookingWindowMonths}
            onChange={(e) => setBookingWindowMonths(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.bookingWindowMonths)}
            helperText={fieldErrors.bookingWindowMonths}
            sx={{ width: 170 }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">months</InputAdornment>,
                sx: { fontFamily: MONO },
              },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Booking window (months)' },
            }}
          />
        </SettingRow>

        {/* Booking lead time (DNKADM-0385) — the booking window's mirror, directly beside it. */}
        <SettingRow
          label="Booking lead time"
          hint={`${LEAD_TIME_BEHAVIOUR} Leave blank for no lead time: members can book right up to the start. A service can override this.`}
        >
          <LeadTimeInput
            id={leadTimeId}
            amount={leadTimeAmount}
            unit={leadTimeUnit}
            onAmountChange={setLeadTimeAmount}
            onUnitChange={setLeadTimeUnit}
            disabled={saving}
            error={Boolean(fieldErrors.leadTimeMinutes)}
            helperText={fieldErrors.leadTimeMinutes}
            amountAriaLabel="Booking lead time"
            amountInputSx={{ fontFamily: MONO }}
            amountWidth={96}
          />
        </SettingRow>

        <SettingRow label="Guest hold" hint="How long a guest slot is held before release">
          <TextField
            id={holdId}
            hiddenLabel
            value={guestHoldTtlMinutes}
            onChange={(e) => setGuestHoldTtlMinutes(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.guestHoldTtlMinutes)}
            helperText={fieldErrors.guestHoldTtlMinutes}
            sx={{ width: 170 }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
                sx: { fontFamily: MONO },
              },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Guest hold (minutes)' },
            }}
          />
        </SettingRow>

        <SettingRow
          label="Auto-action holds"
          hint="Off means no automatic Showed/No-show mark after an un-actioned hold ends"
        >
          <Stack spacing={1} sx={{ width: { xs: 1, sm: 280 } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={holdAutoEnabled}
                  onChange={(event) => setHoldAutoEnabled(event.target.checked)}
                  disabled={saving}
                />
              }
              label="Auto-action un-actioned hold reservations"
            />
            <TextField
              id={holdAutoId}
              hiddenLabel
              value={holdAutoActionMinutes}
              onChange={(e) => setHoldAutoActionMinutes(e.target.value)}
              disabled={saving || !holdAutoEnabled}
              error={Boolean(fieldErrors.holdAutoActionMinutes)}
              helperText={
                fieldErrors.holdAutoActionMinutes ??
                (holdAutoEnabled ? 'Minutes after the reservation ends.' : 'Off sends 0 minutes.')
              }
              sx={{ width: 170 }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">min</InputAdornment>,
                  sx: { fontFamily: MONO },
                },
                htmlInput: { inputMode: 'numeric', 'aria-label': 'Hold auto-action minutes' },
              }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={holdAutoAction}
              onChange={(_event, value: HoldAutoAction | null) => setHoldAutoAction(value)}
              disabled={saving || !holdAutoEnabled}
              aria-label="Hold auto-action"
            >
              <ToggleButton value="SHOWED" sx={{ textTransform: 'none' }}>
                Showed
              </ToggleButton>
              <ToggleButton value="NO_SHOW" sx={{ textTransform: 'none' }}>
                No-show
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2">
              Showed charges the session price. No-show charges the no-show fee.
            </Typography>
            {fieldErrors.holdAutoAction && (
              <Typography role="alert" sx={{ fontSize: 12, color: 'error.main' }}>
                {fieldErrors.holdAutoAction}
              </Typography>
            )}
          </Stack>
        </SettingRow>

        <SettingRow
          label="Country"
          hint="Two-letter country code used for member-facing defaults"
        >
          <TextField
            id={countryId}
            hiddenLabel
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
            disabled={saving}
            error={Boolean(fieldErrors.countryCode)}
            helperText={fieldErrors.countryCode ?? 'Optional — leave blank to keep the current setting.'}
            sx={{ width: 170 }}
            slotProps={{
              input: { sx: { fontFamily: MONO } },
              htmlInput: { maxLength: 2, 'aria-label': 'Country' },
            }}
          />
        </SettingRow>

        <SettingRow
          label="Billing day of month"
          hint="New memberships start charging on this day (1–28). Leave blank to charge at signup. Locations can override. Not used at a location whose card processor cannot anchor."
        >
          <TextField
            id={billingDayId}
            hiddenLabel
            value={billingAnchorDay}
            onChange={(e) => setBillingAnchorDay(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.billingAnchorDay)}
            helperText={fieldErrors.billingAnchorDay}
            sx={{ width: 170 }}
            slotProps={{
              input: { sx: { fontFamily: MONO } },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Billing day of month' },
            }}
          />
        </SettingRow>

        {/* Member-app credit purchases (DNKADM-0409) — saves on its own when flipped. */}
        <SettingRow
          label="Only members can buy credits in the member app"
          hint="When on, people without a membership can't buy credits in the app. Staff can still sell credits at the desk. Packages are not affected."
        >
          <Switch
            checked={creditPurchaseMembersOnly}
            onChange={(event) => void handleCreditSwitch(event.target.checked)}
            disabled={busy}
            slotProps={{
              input: { 'aria-label': 'Only members can buy credits in the member app' },
            }}
          />
        </SettingRow>

        {/* Cancellation policy (DNKADM-0432) — saves on its own when flipped. */}
        <SettingRow
          label="Full refund if we never confirmed"
          hint="When a member cancels a booking your staff have not confirmed yet, they get everything back, even inside your cancellation window."
        >
          <Switch
            checked={unconfirmedCancelFullRefund}
            onChange={(event) => void handleRefundSwitch(event.target.checked)}
            disabled={busy}
            slotProps={{
              input: { 'aria-label': 'Full refund if we never confirmed' },
            }}
          />
        </SettingRow>

        {/* Coach calendar sync provider (DNKADM-0384 U1) — beside the billing day and timezone rows. */}
        <SettingRow
          label="Coach calendar sync"
          hint="Where a coach’s confirmed bookings are mirrored. Off means no coach’s calendar syncs, whatever their own setting says. Each coach picks their calendar on their staff record; changing this keeps those choices."
        >
          <TextField
            id={calendarProviderId}
            select
            hiddenLabel
            value={staffCalendarProvider}
            onChange={(e) => setStaffCalendarProvider(normalizeStaffCalendarProvider(e.target.value))}
            disabled={saving}
            helperText={staffCalendarProviderDescription(staffCalendarProvider)}
            sx={{ width: { xs: 1, sm: 240 } }}
            slotProps={{
              select: { SelectDisplayProps: { 'aria-label': 'Coach calendar sync' } },
            }}
          >
            {STAFF_CALENDAR_PROVIDERS.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {staffCalendarProviderLabel(provider)}
              </MenuItem>
            ))}
          </TextField>
        </SettingRow>

        <SettingRow
          label="Default timezone"
          hint="IANA timezone for the tenant (locations may override per slot)"
          last
        >
          <Autocomplete
            options={timeZoneOptions}
            value={defaultTimezone}
            onChange={(_event, value) => setDefaultTimezone(value)}
            disabled={saving}
            autoHighlight
            disableClearable
            sx={{ width: 240 }}
            renderInput={(params) => (
              <TextField
                {...params}
                id={tzId}
                hiddenLabel
                placeholder="America/New_York"
                error={Boolean(fieldErrors.defaultTimezone)}
                helperText={fieldErrors.defaultTimezone}
                slotProps={{
                  ...params.slotProps,
                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    'aria-label': 'Default timezone',
                  },
                }}
              />
            )}
          />
        </SettingRow>
      </Paper>
      {serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 1.75 }}>
        <Button variant="contained" onClick={() => void handleSave()} disabled={busy}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </Stack>
    </Box>
  );
}

/** One label/hint + control row in the settings card. */
function SettingRow({
  label,
  hint,
  last,
  children,
}: {
  label: string;
  hint: string;
  last?: boolean;
  children: ReactNode;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Box sx={{ flex: 1, pt: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      </Box>
      {children}
    </Stack>
  );
}
